import React from 'react';
import type {
  CharacterStyle,
  SceneSettings,
  ShapeStyle,
  StoryElement,
  TextStyle,
} from '../types';
import { defaultTextStyle } from '../model/factories';
import { textAnchorFor, textXFor, wrapText } from './text';

interface SceneArtProps {
  scene: SceneSettings;
  width: number;
  height: number;
  prefix: string;
}

const timeColours = (time: SceneSettings['time']) => {
  if (time === 'night') return { sky: '#18234d', sky2: '#47578d', light: '#ffd77a', overlay: '#14203f' };
  if (time === 'sunset') return { sky: '#ffb36b', sky2: '#8d65a9', light: '#fff0c8', overlay: '#6c315f' };
  return { sky: '#87d1f5', sky2: '#dff4ff', light: '#fff8d1', overlay: '#ffffff' };
};

const WindowSky = ({ x, y, w, h, scene }: { x: number; y: number; w: number; h: number; scene: SceneSettings }) => {
  const colours = timeColours(scene.time);
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx={8} fill="#fff" />
      <rect x={x + 14} y={y + 14} width={w - 28} height={h - 28} fill={colours.sky} />
      <line x1={x + w / 2} y1={y + 14} x2={x + w / 2} y2={y + h - 14} stroke="#fff" strokeWidth={9} />
      <line x1={x + 14} y1={y + h * 0.62} x2={x + w - 14} y2={y + h * 0.62} stroke="#fff" strokeWidth={9} />
      {scene.time === 'night' && (
        <g fill="#fff9b0">
          <circle cx={x + w * 0.24} cy={y + h * 0.25} r={4} />
          <circle cx={x + w * 0.76} cy={y + h * 0.34} r={3} />
          <circle cx={x + w * 0.6} cy={y + h * 0.18} r={3} />
        </g>
      )}
    </g>
  );
};

export const SceneArt: React.FC<SceneArtProps> = ({ scene, width, height, prefix }) => {
  const colours = timeColours(scene.time);
  const horizon = height * 0.66;
  let content: React.ReactNode;

  switch (scene.assetId) {
    case 'bright-classroom':
      content = (
        <>
          <rect width={width} height={height} fill="#f8efdf" />
          <rect y={height * 0.7} width={width} height={height * 0.3} fill="#b98c66" />
          <WindowSky x={width * 0.05} y={height * 0.08} w={width * 0.23} h={height * 0.32} scene={scene} />
          <rect x={width * 0.4} y={height * 0.08} width={width * 0.48} height={height * 0.34} rx={10} fill="#416f67" stroke="#5a3f2e" strokeWidth={14} />
          <g fill="#ca9360" stroke="#6d472d" strokeWidth={7}>
            {[0.16, 0.44, 0.72].map((x, index) => (
              <g key={index}>
                <rect x={width * x} y={height * 0.57} width={width * 0.2} height={height * 0.08} rx={8} />
                <rect x={width * (x + 0.02)} y={height * 0.65} width={width * 0.025} height={height * 0.21} />
                <rect x={width * (x + 0.155)} y={height * 0.65} width={width * 0.025} height={height * 0.21} />
              </g>
            ))}
          </g>
          <circle cx={width * 0.91} cy={height * 0.16} r={42} fill="#fff" stroke="#574e55" strokeWidth={8} />
          <line x1={width * 0.91} y1={height * 0.16} x2={width * 0.91} y2={height * 0.125} stroke="#574e55" strokeWidth={8} strokeLinecap="round" />
          <line x1={width * 0.91} y1={height * 0.16} x2={width * 0.94} y2={height * 0.18} stroke="#574e55" strokeWidth={8} strokeLinecap="round" />
        </>
      );
      break;
    case 'cosy-lounge':
      content = (
        <>
          <rect width={width} height={height} fill="#f7e8d9" />
          <rect y={height * 0.72} width={width} height={height * 0.28} fill="#9c8068" />
          <WindowSky x={width * 0.07} y={height * 0.09} w={width * 0.25} h={height * 0.35} scene={scene} />
          <rect x={width * 0.38} y={height * 0.49} width={width * 0.43} height={height * 0.25} rx={38} fill="#3ca89b" stroke="#2a5c5a" strokeWidth={10} />
          <rect x={width * 0.36} y={height * 0.54} width={width * 0.08} height={height * 0.23} rx={30} fill="#3ca89b" stroke="#2a5c5a" strokeWidth={10} />
          <rect x={width * 0.76} y={height * 0.54} width={width * 0.08} height={height * 0.23} rx={30} fill="#3ca89b" stroke="#2a5c5a" strokeWidth={10} />
          <rect x={width * 0.5} y={height * 0.6} width={width * 0.12} height={height * 0.11} rx={20} fill="#f2b95c" />
          <g transform={`translate(${width * 0.88} ${height * 0.5})`}>
            <rect x={-25} y={70} width={50} height={125} fill="#bb7148" />
            <circle cx={0} cy={30} r={70} fill="#5caf72" />
          </g>
        </>
      );
      break;
    case 'city-crossing':
      content = (
        <>
          <rect width={width} height={height} fill={colours.sky} />
          <g>
            <rect x={0} y={height * 0.22} width={width * 0.18} height={height * 0.5} fill="#5f76a1" />
            <rect x={width * 0.14} y={height * 0.1} width={width * 0.22} height={height * 0.62} fill="#e27867" />
            <rect x={width * 0.34} y={height * 0.27} width={width * 0.18} height={height * 0.45} fill="#6ca79c" />
            <rect x={width * 0.49} y={height * 0.14} width={width * 0.24} height={height * 0.58} fill="#d8a550" />
            <rect x={width * 0.7} y={height * 0.23} width={width * 0.3} height={height * 0.49} fill="#745989" />
            {Array.from({ length: 24 }).map((_, index) => {
              const col = index % 8;
              const row = Math.floor(index / 8);
              return <rect key={index} x={width * (0.03 + col * 0.12)} y={height * (0.3 + row * 0.13)} width={width * 0.045} height={height * 0.055} fill={scene.time === 'night' ? '#ffd978' : '#b8e7f3'} />;
            })}
          </g>
          <rect y={height * 0.72} width={width} height={height * 0.28} fill="#5d6069" />
          <g fill="#f8f2dd">
            {Array.from({ length: 6 }).map((_, index) => <rect key={index} x={width * (0.06 + index * 0.17)} y={height * 0.83} width={width * 0.1} height={height * 0.05} />)}
          </g>
        </>
      );
      break;
    case 'neighbourhood-park':
      content = (
        <>
          <rect width={width} height={height} fill={colours.sky} />
          <circle cx={width * 0.84} cy={height * 0.18} r={65} fill={scene.time === 'night' ? '#f5e9b5' : '#ffd35b'} />
          <path d={`M0 ${horizon} Q ${width * 0.25} ${height * 0.48}, ${width * 0.5} ${horizon} T ${width} ${horizon}`} fill="#7dbb75" />
          <rect y={horizon} width={width} height={height - horizon} fill="#71aa64" />
          <path d={`M${width * 0.34} ${height} Q${width * 0.48} ${height * 0.73} ${width * 0.62} ${height}`} fill="#d9c49d" />
          {[0.13, 0.82].map((x, index) => (
            <g key={index} transform={`translate(${width * x} ${height * 0.42})`}>
              <rect x={-20} y={90} width={40} height={180} fill="#8b5a3c" />
              <circle cx={0} cy={40} r={105} fill={index ? '#4f9d69' : '#5cac72'} />
              <circle cx={-65} cy={85} r={70} fill="#62b877" />
              <circle cx={70} cy={90} r={65} fill="#4d9c63" />
            </g>
          ))}
          <g stroke="#6c4b35" strokeWidth={10} fill="#c99052">
            <rect x={width * 0.41} y={height * 0.59} width={width * 0.2} height={height * 0.07} rx={8} />
            <line x1={width * 0.43} y1={height * 0.66} x2={width * 0.43} y2={height * 0.79} />
            <line x1={width * 0.59} y1={height * 0.66} x2={width * 0.59} y2={height * 0.79} />
          </g>
        </>
      );
      break;
    case 'deep-forest':
      content = (
        <>
          <rect width={width} height={height} fill={scene.time === 'night' ? '#142a39' : '#8cc7a0'} />
          <rect y={height * 0.72} width={width} height={height * 0.28} fill="#355f42" />
          {Array.from({ length: 9 }).map((_, index) => {
            const x = width * (index / 8);
            const scale = 0.7 + (index % 3) * 0.18;
            return (
              <g key={index} transform={`translate(${x} ${height * 0.28}) scale(${scale})`}>
                <rect x={-22} y={120} width={44} height={330} fill="#6c4631" />
                <path d="M0 0 L-120 240 L120 240 Z" fill={index % 2 ? '#2f8050' : '#3d9460'} />
                <path d="M0 80 L-105 300 L105 300 Z" fill={index % 2 ? '#2a7147' : '#338253'} />
              </g>
            );
          })}
          <path d={`M${width * 0.36} ${height} Q${width * 0.5} ${height * 0.7} ${width * 0.65} ${height}`} fill="#b69972" opacity={0.85} />
        </>
      );
      break;
    case 'sunny-beach':
      content = (
        <>
          <rect width={width} height={height * 0.5} fill={colours.sky} />
          <circle cx={width * 0.84} cy={height * 0.16} r={70} fill={scene.time === 'night' ? '#eee7c6' : '#ffd056'} />
          <rect y={height * 0.5} width={width} height={height * 0.25} fill="#49b9d1" />
          <path d={`M0 ${height * 0.62} Q${width * 0.15} ${height * 0.57} ${width * 0.3} ${height * 0.62} T${width * 0.6} ${height * 0.62} T${width} ${height * 0.62}`} fill="none" stroke="#d9fbff" strokeWidth={18} />
          <rect y={height * 0.75} width={width} height={height * 0.25} fill="#f2d18e" />
          <g transform={`translate(${width * 0.2} ${height * 0.62})`}>
            <line y1={0} y2={180} stroke="#6d4933" strokeWidth={14} />
            <path d="M0 0 L-100 60 A120 120 0 0 1 100 60 Z" fill="#ff6d79" />
            <path d="M0 0 L0 72 A120 120 0 0 0 100 60 Z" fill="#fff3c8" />
          </g>
          <g transform={`translate(${width * 0.72} ${height * 0.78})`}>
            <path d="M0 0 Q50 -45 100 0 L80 35 H20 Z" fill="#ffffff" stroke="#53677c" strokeWidth={7} />
            <path d="M50 -45 V-95 L90 -55 Z" fill="#6255f6" />
          </g>
        </>
      );
      break;
    case 'mountain-valley':
      content = (
        <>
          <rect width={width} height={height} fill={colours.sky} />
          <circle cx={width * 0.82} cy={height * 0.16} r={60} fill={scene.time === 'night' ? '#f2e9bd' : '#ffd45c'} />
          <path d={`M0 ${height * 0.68} L${width * 0.28} ${height * 0.18} L${width * 0.48} ${height * 0.68} Z`} fill="#6f7a8f" />
          <path d={`M${width * 0.25} ${height * 0.68} L${width * 0.56} ${height * 0.1} L${width * 0.83} ${height * 0.68} Z`} fill="#8b91a3" />
          <path d={`M${width * 0.53} ${height * 0.15} L${width * 0.56} ${height * 0.1} L${width * 0.62} ${height * 0.22} L${width * 0.58} ${height * 0.2} L${width * 0.55} ${height * 0.25} Z`} fill="#f7f5ee" />
          <path d={`M${width * 0.6} ${height * 0.68} L${width * 0.83} ${height * 0.28} L${width} ${height * 0.68} Z`} fill="#657087" />
          <rect y={height * 0.68} width={width} height={height * 0.32} fill="#6cae66" />
          <path d={`M${width * 0.4} ${height} Q${width * 0.51} ${height * 0.7} ${width * 0.62} ${height}`} fill="#d4b784" />
        </>
      );
      break;
    case 'orbiting-space':
      content = (
        <>
          <rect width={width} height={height} fill={`url(#${prefix}-space)`} />
          <defs>
            <radialGradient id={`${prefix}-space`} cx="35%" cy="30%">
              <stop offset="0" stopColor="#343277" />
              <stop offset="1" stopColor="#0c1028" />
            </radialGradient>
          </defs>
          {Array.from({ length: 38 }).map((_, index) => (
            <circle key={index} cx={(index * 97) % width} cy={(index * 53) % height} r={index % 5 === 0 ? 4 : 2} fill={index % 3 ? '#ffffff' : '#ffe58d'} opacity={0.85} />
          ))}
          <circle cx={width * 0.72} cy={height * 0.42} r={height * 0.18} fill="#5ca6d8" />
          <path d={`M${width * 0.55} ${height * 0.43} Q${width * 0.72} ${height * 0.27} ${width * 0.9} ${height * 0.45}`} fill="none" stroke="#e7c77c" strokeWidth={24} opacity={0.75} />
          <circle cx={width * 0.2} cy={height * 0.22} r={height * 0.08} fill="#c581df" />
        </>
      );
      break;
    case 'modern-kitchen':
      content = (
        <>
          <rect width={width} height={height} fill="#e9f0ef" />
          <rect y={height * 0.72} width={width} height={height * 0.28} fill="#9e8672" />
          <WindowSky x={width * 0.08} y={height * 0.08} w={width * 0.25} h={height * 0.35} scene={scene} />
          <g>
            <rect x={width * 0.42} y={height * 0.15} width={width * 0.45} height={height * 0.18} rx={10} fill="#f5f3ed" stroke="#9ca8a5" strokeWidth={7} />
            <line x1={width * 0.57} y1={height * 0.15} x2={width * 0.57} y2={height * 0.33} stroke="#9ca8a5" strokeWidth={7} />
            <line x1={width * 0.72} y1={height * 0.15} x2={width * 0.72} y2={height * 0.33} stroke="#9ca8a5" strokeWidth={7} />
            <rect x={width * 0.37} y={height * 0.48} width={width * 0.55} height={height * 0.25} fill="#f7f5ef" stroke="#82908c" strokeWidth={8} />
            <rect x={width * 0.37} y={height * 0.45} width={width * 0.55} height={height * 0.05} fill="#3e4a49" />
            <rect x={width * 0.65} y={height * 0.5} width={width * 0.18} height={height * 0.21} fill="#c8d5d3" stroke="#82908c" strokeWidth={7} />
            <circle cx={width * 0.72} cy={height * 0.6} r={8} fill="#5c6867" />
            <circle cx={width * 0.76} cy={height * 0.6} r={8} fill="#5c6867" />
          </g>
          <rect x={width * 0.14} y={height * 0.57} width={width * 0.18} height={height * 0.16} rx={10} fill="#5d8f92" />
        </>
      );
      break;
    case 'theatre-stage':
      content = (
        <>
          <rect width={width} height={height} fill="#3e2c5c" />
          <path d={`M0 0 H${width * 0.33} L${width * 0.22} ${height * 0.76} H0 Z`} fill="#ab2858" />
          <path d={`M${width} 0 H${width * 0.67} L${width * 0.78} ${height * 0.76} H${width} Z`} fill="#ab2858" />
          <path d={`M0 0 Q${width * 0.5} ${height * 0.2} ${width} 0 V${height * 0.08} Q${width * 0.5} ${height * 0.27} 0 ${height * 0.08} Z`} fill="#c13c69" />
          <path d={`M${width * 0.24} ${height * 0.76} Q${width * 0.5} ${height * 0.69} ${width * 0.76} ${height * 0.76} V${height} H${width * 0.24} Z`} fill="#8f765e" />
          <rect y={height * 0.86} width={width} height={height * 0.14} fill="#6f4733" />
          <ellipse cx={width * 0.5} cy={height * 0.72} rx={width * 0.22} ry={height * 0.12} fill="#6c4f83" opacity={0.7} />
        </>
      );
      break;
    case 'colour-wash':
      content = (
        <>
          <defs>
            <linearGradient id={`${prefix}-wash`} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor="#6255f6" />
              <stop offset="0.45" stopColor="#c45f9b" />
              <stop offset="1" stopColor="#ffb34c" />
            </linearGradient>
          </defs>
          <rect width={width} height={height} fill={`url(#${prefix}-wash)`} />
          <circle cx={width * 0.2} cy={height * 0.25} r={height * 0.23} fill="#fff" opacity={0.09} />
          <circle cx={width * 0.8} cy={height * 0.7} r={height * 0.32} fill="#1f163d" opacity={0.12} />
          <path d={`M0 ${height * 0.8} Q${width * 0.35} ${height * 0.55} ${width} ${height * 0.72} V${height} H0 Z`} fill="#fff" opacity={0.08} />
        </>
      );
      break;
    case 'paper-grid':
      content = (
        <>
          <defs>
            <pattern id={`${prefix}-grid`} width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M40 0 H0 V40" fill="none" stroke="#d8d3e4" strokeWidth="2" />
            </pattern>
          </defs>
          <rect width={width} height={height} fill="#faf9fd" />
          <rect width={width} height={height} fill={`url(#${prefix}-grid)`} />
          <rect x={width * 0.055} y={height * 0.07} width={width * 0.008} height={height * 0.86} rx={8} fill="#ff7b89" opacity={0.5} />
        </>
      );
      break;
    case 'blank-white':
      content = <rect width={width} height={height} fill="#ffffff" />;
      break;
    case 'studio-loft':
    default:
      content = (
        <>
          <rect width={width} height={height} fill="#e8f4f2" />
          <rect y={height * 0.76} width={width} height={height * 0.24} fill="#9a856f" />
          <WindowSky x={width * 0.06} y={height * 0.09} w={width * 0.28} h={height * 0.38} scene={scene} />
          <g>
            <rect x={width * 0.58} y={height * 0.12} width={width * 0.28} height={height * 0.24} rx={20} fill="#fff" opacity={0.92} />
            <polyline points={`${width * 0.61},${height * 0.31} ${width * 0.68},${height * 0.24} ${width * 0.75},${height * 0.28} ${width * 0.83},${height * 0.18}`} fill="none" stroke="#6758ff" strokeWidth={12} strokeLinecap="round" strokeLinejoin="round" />
          </g>
          <rect x={width * 0.12} y={height * 0.55} width={width * 0.3} height={height * 0.08} rx={10} fill="#d6a166" stroke="#765033" strokeWidth={8} />
          <rect x={width * 0.58} y={height * 0.55} width={width * 0.3} height={height * 0.08} rx={10} fill="#d6a166" stroke="#765033" strokeWidth={8} />
          <g transform={`translate(${width * 0.5} ${height * 0.58})`}>
            <rect x={-30} y={65} width={60} height={90} fill="#bb7246" />
            <circle cx={0} cy={20} r={72} fill="#63b77c" />
          </g>
        </>
      );
      break;
  }

  return (
    <g>
      {content}
      {scene.time !== 'day' && <rect width={width} height={height} fill={colours.overlay} opacity={scene.time === 'night' ? 0.26 : 0.08} />}
      {scene.weather === 'rain' && (
        <g stroke="#b7e7ff" strokeWidth={5} opacity={0.8 * scene.intensity}>
          {Array.from({ length: 32 }).map((_, index) => {
            const x = (index * 79) % width;
            const y = (index * 113) % height;
            return <line key={index} x1={x} y1={y} x2={x - 18} y2={y + 45} />;
          })}
        </g>
      )}
      {scene.weather === 'snow' && (
        <g fill="#fff" opacity={0.9 * scene.intensity}>
          {Array.from({ length: 42 }).map((_, index) => (
            <circle key={index} cx={(index * 91) % width} cy={(index * 67) % height} r={4 + (index % 4)} />
          ))}
        </g>
      )}
    </g>
  );
};

const Arm = ({ x, y, angle, sleeve, skin, length = 95 }: { x: number; y: number; angle: number; sleeve: string; skin: string; length?: number }) => (
  <g transform={`translate(${x} ${y}) rotate(${angle})`}>
    <line x1={0} y1={0} x2={0} y2={length * 0.46} stroke={sleeve} strokeWidth={30} strokeLinecap="round" />
    <line x1={0} y1={length * 0.38} x2={0} y2={length} stroke={skin} strokeWidth={20} strokeLinecap="round" />
    <circle cx={0} cy={length + 3} r={12} fill={skin} />
  </g>
);

const expressionFace = (style: CharacterStyle) => {
  const eyeY = 83;
  const mouthY = 105;
  return (
    <g stroke="#2c2230" strokeLinecap="round">
      {style.expression === 'surprised' ? (
        <>
          <circle cx={78} cy={eyeY} r={5} fill="#2c2230" />
          <circle cx={122} cy={eyeY} r={5} fill="#2c2230" />
          <circle cx={100} cy={mouthY + 3} r={9} fill="none" strokeWidth={4} />
        </>
      ) : (
        <>
          <circle cx={78} cy={eyeY} r={4.5} fill="#2c2230" />
          <circle cx={122} cy={eyeY} r={4.5} fill="#2c2230" />
          {style.expression === 'happy' && <path d={`M78 ${mouthY} Q100 ${mouthY + 22} 122 ${mouthY}`} fill="none" strokeWidth={5} />}
          {style.expression === 'neutral' && <line x1={84} y1={mouthY + 8} x2={116} y2={mouthY + 8} strokeWidth={5} />}
          {style.expression === 'sad' && <path d={`M80 ${mouthY + 18} Q100 ${mouthY - 2} 120 ${mouthY + 18}`} fill="none" strokeWidth={5} />}
          {style.expression === 'angry' && (
            <>
              <path d="M68 70 L86 76" strokeWidth={5} />
              <path d="M132 70 L114 76" strokeWidth={5} />
              <path d={`M82 ${mouthY + 16} Q100 ${mouthY} 118 ${mouthY + 16}`} fill="none" strokeWidth={5} />
            </>
          )}
        </>
      )}
    </g>
  );
};

export const CharacterArt = ({ assetId = 'ava', style }: { assetId?: string; style: CharacterStyle }) => {
  const isChild = assetId === 'zoe' || assetId === 'sam';
  const scale = isChild ? 0.9 : 1;
  const torsoY = 142;
  const armAngles = (() => {
    switch (style.pose) {
      case 'wave': return { left: 18, right: -150 };
      case 'point': return { left: 16, right: -92 };
      case 'celebrate': return { left: 155, right: -155 };
      case 'sit': return { left: 18, right: -18 };
      default: return { left: 18, right: -18 };
    }
  })();
  const side = style.view === 'side';
  const hairStyle = assetId === 'noah' || assetId === 'sam' ? 'short' : assetId === 'professor' ? 'professor' : assetId === 'riley' ? 'wave' : 'long';

  return (
    <g transform={`translate(${100 * (1 - scale)} ${360 * (1 - scale)}) scale(${scale})`}>
      {style.pose === 'sit' ? (
        <g>
          <line x1={82} y1={270} x2={55} y2={335} stroke={style.bottom} strokeWidth={27} strokeLinecap="round" />
          <line x1={118} y1={270} x2={150} y2={325} stroke={style.bottom} strokeWidth={27} strokeLinecap="round" />
          <line x1={53} y1={337} x2={25} y2={337} stroke={style.shoes} strokeWidth={16} strokeLinecap="round" />
          <line x1={150} y1={327} x2={178} y2={327} stroke={style.shoes} strokeWidth={16} strokeLinecap="round" />
        </g>
      ) : (
        <g>
          <line x1={82} y1={264} x2={75} y2={339} stroke={style.bottom} strokeWidth={28} strokeLinecap="round" />
          <line x1={118} y1={264} x2={125} y2={339} stroke={style.bottom} strokeWidth={28} strokeLinecap="round" />
          <line x1={73} y1={341} x2={50} y2={341} stroke={style.shoes} strokeWidth={15} strokeLinecap="round" />
          <line x1={127} y1={341} x2={150} y2={341} stroke={style.shoes} strokeWidth={15} strokeLinecap="round" />
        </g>
      )}
      <Arm x={61} y={torsoY + 35} angle={armAngles.left} sleeve={style.top} skin={style.skin} />
      <Arm x={139} y={torsoY + 35} angle={armAngles.right} sleeve={style.top} skin={style.skin} />
      <path d="M61 145 Q100 125 139 145 L130 268 Q100 288 70 268 Z" fill={style.top} stroke="#3b2a2e" strokeWidth={6} />
      <rect x={78} y={252} width={44} height={23} rx={9} fill={style.bottom} />
      <circle cx={100} cy={90} r={52} fill={style.skin} stroke="#3b2a2e" strokeWidth={6} />
      {hairStyle === 'short' && <path d="M54 83 Q55 35 99 31 Q150 34 150 85 Q129 58 103 63 Q75 58 54 83 Z" fill={style.hair} />}
      {hairStyle === 'long' && (
        <>
          <path d="M51 88 Q46 35 98 29 Q154 31 153 94 L145 144 Q132 124 136 87 Q114 54 70 67 Q69 115 57 143 Z" fill={style.hair} />
          {assetId === 'maya' && <circle cx={147} cy={97} r={29} fill={style.hair} />}
        </>
      )}
      {hairStyle === 'wave' && <path d="M51 87 Q53 31 102 30 Q153 30 152 90 Q140 62 122 62 Q100 42 79 65 Q61 61 51 87 Z" fill={style.hair} />}
      {hairStyle === 'professor' && (
        <>
          <path d="M54 76 Q59 35 100 34 Q140 34 148 76 Q134 53 117 57 Q98 44 80 58 Q63 53 54 76 Z" fill={style.hair} />
          <g fill="none" stroke="#29243a" strokeWidth={4}>
            <circle cx={78} cy={84} r={15} />
            <circle cx={122} cy={84} r={15} />
            <line x1={93} y1={84} x2={107} y2={84} />
          </g>
        </>
      )}
      {assetId === 'zoe' && <g fill={style.hair}><circle cx={50} cy={90} r={20} /><circle cx={150} cy={90} r={20} /></g>}
      {!side && expressionFace(style)}
      {side && (
        <g>
          <circle cx={120} cy={82} r={5} fill="#2c2230" />
          <path d="M131 98 Q137 105 129 112" fill="none" stroke="#2c2230" strokeWidth={4} strokeLinecap="round" />
        </g>
      )}
    </g>
  );
};

export const PropArt = ({ assetId = 'laptop' }: { assetId?: string }) => {
  switch (assetId) {
    case 'phone':
      return <g><rect x={55} y={5} width={90} height={145} rx={18} fill="#28263b" /><rect x={64} y={20} width={72} height={108} rx={7} fill="#7ed0ea" /><circle cx={100} cy={139} r={6} fill="#a9a4bb" /></g>;
    case 'tablet':
      return <g><rect x={35} y={5} width={130} height={145} rx={16} fill="#2f3046" /><rect x={45} y={17} width={110} height={112} rx={7} fill="#c7eef5" /><circle cx={100} cy={140} r={5} fill="#a9a4bb" /></g>;
    case 'lightbulb':
      return <g><circle cx={100} cy={58} r={48} fill="#ffd95a" stroke="#5f4b25" strokeWidth={7} /><path d="M70 54 Q100 78 130 54" fill="none" stroke="#fff4a8" strokeWidth={8} /><rect x={78} y={102} width={44} height={28} rx={8} fill="#8a7b77" /><line x1={82} y1={136} x2={118} y2={136} stroke="#574c4a" strokeWidth={9} strokeLinecap="round" /></g>;
    case 'coffee':
      return <g><path d="M45 45 H135 V120 Q90 145 45 120 Z" fill="#fff3e0" stroke="#6a4b39" strokeWidth={7} /><path d="M135 60 Q178 60 166 102 Q153 125 135 107" fill="none" stroke="#6a4b39" strokeWidth={10} /><ellipse cx={90} cy={45} rx={45} ry={13} fill="#6b402c" /><path d="M75 30 Q65 10 82 0M105 30 Q95 10 112 0" fill="none" stroke="#b5a39a" strokeWidth={5} strokeLinecap="round" /></g>;
    case 'book-stack':
      return <g><rect x={28} y={96} width={150} height={35} rx={7} fill="#5f89d8" stroke="#332b43" strokeWidth={6} /><rect x={18} y={58} width={145} height={38} rx={7} fill="#f7c44f" stroke="#332b43" strokeWidth={6} /><rect x={38} y={20} width={142} height={38} rx={7} fill="#e87878" stroke="#332b43" strokeWidth={6} /><line x1={56} y1={76} x2={128} y2={76} stroke="#fff4c8" strokeWidth={5} /><line x1={70} y1={38} x2={145} y2={38} stroke="#ffe2d8" strokeWidth={5} /></g>;
    case 'plant':
      return <g><rect x={72} y={95} width={58} height={55} fill="#bd7146" /><circle cx={100} cy={72} r={50} fill="#56ad6c" /><circle cx={65} cy={78} r={30} fill="#6abd7a" /><circle cx={135} cy={78} r={30} fill="#4b9d60" /><line x1={100} y1={105} x2={100} y2={48} stroke="#397a4c" strokeWidth={8} /></g>;
    case 'desk':
      return <g><rect x={7} y={30} width={186} height={45} rx={7} fill="#cf9860" stroke="#6f4a31" strokeWidth={7} /><rect x={25} y={74} width={18} height={76} fill="#81583c" /><rect x={157} y={74} width={18} height={76} fill="#81583c" /></g>;
    case 'chair':
      return <g><rect x={55} y={10} width={90} height={80} rx={12} fill="#7b6cff" stroke="#37324f" strokeWidth={7} /><rect x={48} y={85} width={104} height={35} rx={10} fill="#6557db" stroke="#37324f" strokeWidth={7} /><line x1={65} y1={118} x2={55} y2={150} stroke="#37324f" strokeWidth={10} /><line x1={135} y1={118} x2={145} y2={150} stroke="#37324f" strokeWidth={10} /></g>;
    case 'whiteboard':
      return <g><rect x={10} y={10} width={180} height={105} rx={8} fill="#fff" stroke="#5c5a64" strokeWidth={8} /><polyline points="35,85 72,58 106,72 160,35" fill="none" stroke="#6255f6" strokeWidth={8} strokeLinejoin="round" /><line x1={55} y1={115} x2={45} y2={150} stroke="#5c5a64" strokeWidth={9} /><line x1={145} y1={115} x2={155} y2={150} stroke="#5c5a64" strokeWidth={9} /></g>;
    case 'chart':
      return <g><rect x={5} y={10} width={190} height={125} rx={12} fill="#fff" stroke="#dfdce8" strokeWidth={5} /><polyline points="22,105 62,75 105,91 150,45 180,25" fill="none" stroke="#6758ff" strokeWidth={10} strokeLinecap="round" strokeLinejoin="round" /></g>;
    case 'clock':
      return <g><circle cx={100} cy={77} r={62} fill="#fff" stroke="#4d4a5b" strokeWidth={9} /><line x1={100} y1={77} x2={100} y2={38} stroke="#4d4a5b" strokeWidth={8} strokeLinecap="round" /><line x1={100} y1={77} x2={133} y2={95} stroke="#4d4a5b" strokeWidth={8} strokeLinecap="round" /><circle cx={100} cy={77} r={8} fill="#6758ff" /></g>;
    case 'camera':
      return <g><rect x={20} y={38} width={165} height={95} rx={15} fill="#343247" /><rect x={48} y={22} width={55} height={25} rx={8} fill="#343247" /><circle cx={105} cy={85} r={40} fill="#7cc0d9" stroke="#e5edf1" strokeWidth={9} /><circle cx={105} cy={85} r={18} fill="#2f5970" /><circle cx={160} cy={58} r={8} fill="#ff6e80" /></g>;
    case 'megaphone':
      return <g><path d="M25 55 L145 20 V120 L25 86 Z" fill="#ff6d7d" stroke="#4a3340" strokeWidth={7} /><rect x={17} y={52} width={35} height={39} rx={8} fill="#f3ece4" stroke="#4a3340" strokeWidth={7} /><path d="M60 88 L90 88 L76 145 H47 Z" fill="#4f86f7" stroke="#4a3340" strokeWidth={7} /><path d="M160 35 Q190 70 160 106" fill="none" stroke="#6255f6" strokeWidth={8} strokeLinecap="round" /></g>;
    case 'rocket':
      return <g><path d="M100 5 Q150 45 133 112 L100 135 L67 112 Q50 45 100 5 Z" fill="#f4f3ef" stroke="#443b54" strokeWidth={7} /><circle cx={100} cy={63} r={20} fill="#6ac5e4" stroke="#443b54" strokeWidth={6} /><path d="M66 90 L35 125 L70 118 Z" fill="#6255f6" stroke="#443b54" strokeWidth={6} /><path d="M134 90 L165 125 L130 118 Z" fill="#6255f6" stroke="#443b54" strokeWidth={6} /><path d="M82 130 L100 155 L118 130" fill="#ffb43f" /></g>;
    case 'car':
      return <g><path d="M28 80 L55 40 H143 L174 80 L188 88 V125 H15 V92 Z" fill="#4f86f7" stroke="#37334b" strokeWidth={7} /><path d="M70 48 H130 L150 80 H53 Z" fill="#bce7f5" /><circle cx={55} cy={128} r={22} fill="#302e3d" /><circle cx={150} cy={128} r={22} fill="#302e3d" /><circle cx={55} cy={128} r={9} fill="#a9a5b4" /><circle cx={150} cy={128} r={9} fill="#a9a5b4" /></g>;
    case 'bus':
      return <g><rect x={12} y={25} width={176} height={105} rx={18} fill="#f2bd3f" stroke="#443b42" strokeWidth={7} /><rect x={25} y={38} width={138} height={45} rx={5} fill="#bfe5ef" /><line x1={70} y1={38} x2={70} y2={83} stroke="#443b42" strokeWidth={5} /><line x1={116} y1={38} x2={116} y2={83} stroke="#443b42" strokeWidth={5} /><circle cx={52} cy={131} r={20} fill="#302e3d" /><circle cx={150} cy={131} r={20} fill="#302e3d" /></g>;
    case 'tree':
      return <g><rect x={86} y={80} width={30} height={70} fill="#875637" /><circle cx={100} cy={55} r={58} fill="#4fae65" /><circle cx={55} cy={75} r={35} fill="#68bd76" /><circle cx={145} cy={78} r={35} fill="#419657" /></g>;
    case 'signpost':
      return <g><rect x={90} y={20} width={20} height={130} fill="#805333" /><path d="M25 30 H145 L177 55 L145 80 H25 Z" fill="#f0c158" stroke="#5f4434" strokeWidth={6} /><path d="M175 88 H55 L23 113 L55 138 H175 Z" fill="#6dc1b2" stroke="#5f4434" strokeWidth={6} /></g>;
    case 'backpack':
      return <g><path d="M52 55 Q52 15 100 15 Q148 15 148 55 V138 H52 Z" fill="#6255f6" stroke="#383147" strokeWidth={7} /><rect x={66} y={88} width={68} height={42} rx={13} fill="#8175ff" stroke="#383147" strokeWidth={6} /><path d="M58 65 Q25 76 40 132" fill="none" stroke="#383147" strokeWidth={9} /><path d="M142 65 Q175 76 160 132" fill="none" stroke="#383147" strokeWidth={9} /></g>;
    case 'gift':
      return <g><rect x={35} y={55} width={130} height={88} rx={8} fill="#ff6f91" stroke="#493241" strokeWidth={7} /><rect x={25} y={40} width={150} height={35} rx={7} fill="#ff8ba5" stroke="#493241" strokeWidth={7} /><rect x={90} y={40} width={20} height={103} fill="#ffd04f" /><path d="M100 40 Q54 25 65 5 Q85 -5 100 35 Q115 -5 135 5 Q146 25 100 40 Z" fill="#ffd04f" stroke="#493241" strokeWidth={5} /></g>;
    case 'star':
      return <path d="M100 4 L124 54 L180 62 L139 101 L150 155 L100 129 L50 155 L61 101 L20 62 L76 54 Z" fill="#ffd14f" stroke="#493f32" strokeWidth={7} />;
    case 'heart':
      return <path d="M100 145 C82 121 25 87 27 48 C29 8 77 2 100 35 C123 2 171 8 173 48 C175 87 118 121 100 145 Z" fill="#ff6681" stroke="#4d3140" strokeWidth={7} />;
    case 'cloud':
      return <path d="M45 120 C8 117 7 65 44 58 C49 20 97 8 119 38 C151 25 181 47 176 79 C205 91 194 128 163 128 H49 Z" fill="#e9f6ff" stroke="#526b7c" strokeWidth={7} />;
    case 'laptop':
    default:
      return <g><rect x={32} y={12} width={136} height={94} rx={8} fill="#3c4053" /><rect x={43} y={23} width={114} height={72} rx={3} fill="#82d1e8" /><path d="M15 108 H185 L170 140 H30 Z" fill="#9aa2b4" stroke="#454958" strokeWidth={6} /><rect x={85} y={116} width={30} height={7} rx={3} fill="#5f6676" /></g>;
  }
};

const BubbleText = ({ text, width, height, style }: { text: string; width: number; height: number; style: TextStyle }) => {
  const innerWidth = Math.max(20, width - style.padding * 2);
  const lines = wrapText(text, innerWidth, style.fontSize);
  const lineHeight = style.fontSize * style.lineHeight;
  const total = lines.length * lineHeight;
  const startY = height / 2 - total / 2 + style.fontSize * 0.82;
  return (
    <text
      x={textXFor(style.align, 0, width, style.padding)}
      y={startY}
      textAnchor={textAnchorFor(style.align)}
      fill={style.fill}
      fontFamily={style.fontFamily}
      fontSize={style.fontSize}
      fontWeight={style.fontWeight}
      fontStyle={style.italic ? 'italic' : 'normal'}
      textDecoration={style.underline ? 'underline' : 'none'}
    >
      {lines.map((line, index) => <tspan key={`${line}-${index}`} x={textXFor(style.align, 0, width, style.padding)} dy={index === 0 ? 0 : lineHeight}>{line || ' '}</tspan>)}
    </text>
  );
};

export const BubbleArt = ({ element }: { element: StoryElement }) => {
  const width = element.width;
  const height = element.height;
  const style = element.textStyle ?? defaultTextStyle();
  const kind = element.bubbleStyle ?? 'speech';
  const fill = style.background === 'transparent' ? '#ffffff' : style.background;
  const stroke = style.border === 'transparent' ? '#27243c' : style.border;
  const strokeDasharray = kind === 'whisper' ? '14 12' : undefined;
  const bodyHeight = kind === 'caption' ? height : height * 0.78;

  if (kind === 'thought') {
    return (
      <g>
        <rect x={8} y={8} width={width - 16} height={height * 0.7} rx={Math.min(80, height * 0.25)} fill={fill} stroke={stroke} strokeWidth={style.borderWidth || 5} />
        <circle cx={width * 0.27} cy={height * 0.79} r={height * 0.08} fill={fill} stroke={stroke} strokeWidth={style.borderWidth || 5} />
        <circle cx={width * 0.18} cy={height * 0.92} r={height * 0.045} fill={fill} stroke={stroke} strokeWidth={style.borderWidth || 5} />
        <g transform={`translate(0 0) scale(1 ${0.72})`}><BubbleText text={element.text ?? ''} width={width} height={height} style={style} /></g>
      </g>
    );
  }

  if (kind === 'shout') {
    const points = [
      [width * 0.06, height * 0.3], [width * 0.19, height * 0.25], [width * 0.13, height * 0.08], [width * 0.34, height * 0.17],
      [width * 0.46, height * 0.02], [width * 0.57, height * 0.17], [width * 0.8, height * 0.08], [width * 0.76, height * 0.27],
      [width * 0.96, height * 0.36], [width * 0.8, height * 0.51], [width * 0.92, height * 0.72], [width * 0.69, height * 0.7],
      [width * 0.55, height * 0.94], [width * 0.43, height * 0.74], [width * 0.18, height * 0.83], [width * 0.23, height * 0.62],
      [width * 0.04, height * 0.55],
    ].map((point) => point.join(',')).join(' ');
    return <g><polygon points={points} fill={fill} stroke={stroke} strokeWidth={style.borderWidth || 5} strokeLinejoin="round" /><BubbleText text={element.text ?? ''} width={width} height={height} style={style} /></g>;
  }

  return (
    <g>
      <rect x={5} y={5} width={width - 10} height={bodyHeight - 10} rx={kind === 'caption' ? style.radius : Math.max(style.radius, 30)} fill={fill} stroke={stroke} strokeWidth={style.borderWidth || 5} strokeDasharray={strokeDasharray} />
      {kind !== 'caption' && <path d={`M${width * 0.34} ${bodyHeight - 2} L${width * 0.23} ${height - 5} L${width * 0.48} ${bodyHeight - 4} Z`} fill={fill} stroke={stroke} strokeWidth={style.borderWidth || 5} strokeLinejoin="round" />}
      <g transform={kind === 'caption' ? undefined : `scale(1 ${bodyHeight / height})`}><BubbleText text={element.text ?? ''} width={width} height={height} style={style} /></g>
    </g>
  );
};

export const TextArt = ({ element }: { element: StoryElement }) => {
  const style = element.textStyle ?? defaultTextStyle();
  const lines = wrapText(element.text ?? '', Math.max(10, element.width - style.padding * 2), style.fontSize);
  const lineHeight = style.fontSize * style.lineHeight;
  const total = lines.length * lineHeight;
  const y = element.height / 2 - total / 2 + style.fontSize * 0.82;
  return (
    <g>
      {style.background !== 'transparent' && <rect width={element.width} height={element.height} rx={style.radius} fill={style.background} stroke={style.border} strokeWidth={style.borderWidth} />}
      <text
        x={textXFor(style.align, 0, element.width, style.padding)}
        y={y}
        textAnchor={textAnchorFor(style.align)}
        fill={style.fill}
        fontFamily={style.fontFamily}
        fontSize={style.fontSize}
        fontWeight={style.fontWeight}
        fontStyle={style.italic ? 'italic' : 'normal'}
        textDecoration={style.underline ? 'underline' : 'none'}
      >
        {lines.map((line, index) => <tspan key={`${line}-${index}`} x={textXFor(style.align, 0, element.width, style.padding)} dy={index === 0 ? 0 : lineHeight}>{line || ' '}</tspan>)}
      </text>
    </g>
  );
};

const starPoints = (cx: number, cy: number, outer: number, inner: number, points = 5): string => {
  const values: string[] = [];
  for (let i = 0; i < points * 2; i += 1) {
    const radius = i % 2 === 0 ? outer : inner;
    const angle = -Math.PI / 2 + (i * Math.PI) / points;
    values.push(`${cx + Math.cos(angle) * radius},${cy + Math.sin(angle) * radius}`);
  }
  return values.join(' ');
};

export const ShapeArt = ({ element }: { element: StoryElement }) => {
  const style = element.shapeStyle ?? 'rectangle';
  const appearance = element.appearance ?? { fill: '#7b6cff', stroke: '#27243c', strokeWidth: 8 };
  const { width, height } = element;
  const common = { fill: appearance.fill, stroke: appearance.stroke, strokeWidth: appearance.strokeWidth };
  switch (style) {
    case 'ellipse': return <ellipse cx={width / 2} cy={height / 2} rx={Math.max(1, width / 2 - appearance.strokeWidth)} ry={Math.max(1, height / 2 - appearance.strokeWidth)} {...common} />;
    case 'star': return <polygon points={starPoints(width / 2, height / 2, Math.min(width, height) * 0.47, Math.min(width, height) * 0.22)} {...common} strokeLinejoin="round" />;
    case 'heart': return <path d={`M${width / 2} ${height * 0.92} C${width * 0.38} ${height * 0.75}, ${width * 0.08} ${height * 0.55}, ${width * 0.1} ${height * 0.27} C${width * 0.12} ${height * 0.02}, ${width * 0.4} ${height * 0.01}, ${width / 2} ${height * 0.23} C${width * 0.6} ${height * 0.01}, ${width * 0.88} ${height * 0.02}, ${width * 0.9} ${height * 0.27} C${width * 0.92} ${height * 0.55}, ${width * 0.62} ${height * 0.75}, ${width / 2} ${height * 0.92} Z`} {...common} />;
    case 'line': return <line x1={appearance.strokeWidth} y1={height / 2} x2={width - appearance.strokeWidth} y2={height / 2} stroke={appearance.stroke} strokeWidth={appearance.strokeWidth} strokeLinecap="round" />;
    case 'arrow': return <g><line x1={appearance.strokeWidth} y1={height / 2} x2={width - height * 0.38} y2={height / 2} stroke={appearance.stroke} strokeWidth={appearance.strokeWidth} strokeLinecap="round" /><path d={`M${width - height * 0.52} ${height * 0.16} L${width - appearance.strokeWidth} ${height / 2} L${width - height * 0.52} ${height * 0.84} Z`} fill={appearance.fill === 'transparent' ? appearance.stroke : appearance.fill} stroke={appearance.stroke} strokeWidth={appearance.strokeWidth} strokeLinejoin="round" /></g>;
    case 'rectangle':
    default: return <rect x={appearance.strokeWidth / 2} y={appearance.strokeWidth / 2} width={Math.max(1, width - appearance.strokeWidth)} height={Math.max(1, height - appearance.strokeWidth)} rx={Math.min(30, height * 0.18)} {...common} />;
  }
};

export const ElementArt = ({ element }: { element: StoryElement }) => {
  const transform = `translate(${element.x + element.width / 2} ${element.y + element.height / 2}) rotate(${element.rotation}) scale(${element.flipX ? -1 : 1} ${element.flipY ? -1 : 1}) translate(${-element.width / 2} ${-element.height / 2})`;
  let content: React.ReactNode;
  switch (element.kind) {
    case 'character':
      content = <svg width={element.width} height={element.height} viewBox="0 0 200 360" preserveAspectRatio="xMidYMid meet"><CharacterArt assetId={element.assetId} style={element.character!} /></svg>;
      break;
    case 'prop':
      content = <svg width={element.width} height={element.height} viewBox="0 0 200 160" preserveAspectRatio="xMidYMid meet"><PropArt assetId={element.assetId} /></svg>;
      break;
    case 'bubble':
      content = <BubbleArt element={element} />;
      break;
    case 'shape':
      content = <ShapeArt element={element} />;
      break;
    case 'image':
      content = <image href={element.dataUrl} width={element.width} height={element.height} preserveAspectRatio="xMidYMid meet" />;
      break;
    case 'text':
    default:
      content = <TextArt element={element} />;
      break;
  }
  return <g data-element-id={element.id} transform={transform} opacity={element.opacity} visibility={element.hidden ? 'hidden' : 'visible'}>{content}</g>;
};
